import { useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { linkClick } from '../../components/routerLink';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AddIcon from '@mui/icons-material/Add';
import AddLinkIcon from '@mui/icons-material/AddLink';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { addOutlineSx } from '../../components/addAction';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import type { Subject } from '@ast-data/shared';
import { useAbility, scopedSubject } from '../auth/abilityContext';
// Same icons the office menu uses for these entities (Layout.tsx).
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import FoundationIcon from '@mui/icons-material/Foundation';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { mdiFlask } from '@mdi/js';
import MdiIcon from '../../components/MdiIcon';
import {
    useDescriptionReferences,
    TYPE_ORDER,
    type RefRecord,
    type RefType
} from './useDescriptionReferences';
import ConfirmDialog from '../../components/ConfirmDialog';
import { linkSearchFilter } from '../../components/linkSearch';

// Number and second line both searched; "123 2" finds Fundzettel 123_2.
const REF_SEARCH = linkSearchFilter((option: RefRecord) => [
    option.primary,
    option.secondary ?? ''
]);

interface DescriptionReferencesSectionProps {
    /** The Planum/Profil id whose inbound references are shown. */
    descriptionId: string;
    /** The Befund this description belongs to — what a Gesamtbeschreibung covers. */
    findingId: string;
    /** That Befund's number, to name it in the removal warning. */
    findingNumber: string;
    /** Which reference array to read/write on the referencing records. */
    kind: 'planum' | 'profile';
    /** Scopes the family lookup (references may live in another phase's project). */
    projectId: string;
    /** Navigate to a referenced record's editor — guarded against unsaved edits. */
    onNavigate: (to: string) => void;
    /** Whether the user may add/remove references (edits the target records). */
    canEdit: boolean;
}

const TYPE_META: Record<
    RefType,
    { typeLabelId: string; icon: ReactNode; subject: Subject }
> = {
    photo: {
        typeLabelId: 'nav.photos',
        icon: <PhotoCameraIcon fontSize="small" />,
        subject: 'Photo'
    },
    drawingSheet: {
        typeLabelId: 'nav.drawingSheets',
        icon: <ArchitectureIcon fontSize="small" />,
        subject: 'DrawingSheet'
    },
    overallDescription: {
        typeLabelId: 'nav.overallDescriptions',
        icon: <FoundationIcon fontSize="small" />,
        subject: 'OverallDescription'
    },
    find: {
        typeLabelId: 'nav.finds',
        icon: <EmojiEventsIcon fontSize="small" />,
        subject: 'Find'
    },
    sample: {
        typeLabelId: 'nav.samples',
        icon: <MdiIcon path={mdiFlask} fontSize="small" />,
        subject: 'Sample'
    }
};

/**
 * The record types that reference ONE Befund at most. Adding this description
 * to one replaces the link it has, after asking.
 */
const SINGLE_REF_TYPES: readonly RefType[] = ['find', 'sample'];

/**
 * The Verweise section on a Planum/Profil editor: per record type — photos,
 * drawing sheets, Gesamtbeschreibungen, finds and samples — it lists the records that
 * reference this description (via their `planumIds`/`profileIds`), the inverse of
 * those records' Befund pickers, family-scoped (a reference may live in another
 * phase's project). When `canEdit`, every type shows an "add" button with two
 * ways in: link an existing record (an autocomplete of that type's not-yet-linked
 * ones) or create a new one, whose editor opens already referencing this
 * description. Linking and unlinking write straight to the target record
 * (persisted at once, not through this editor's save). Shown only for a saved
 * description.
 */
export default function DescriptionReferencesSection({
    descriptionId,
    findingId,
    findingNumber,
    kind,
    projectId,
    onNavigate,
    canEdit
}: DescriptionReferencesSectionProps) {
    const intl = useIntl();
    const {
        byType,
        linkedTotal,
        collectionByType,
        idField,
        overallDescriptionRefs,
        newRecordTo
    } = useDescriptionReferences({
        descriptionId,
        findingId,
        kind,
        projectId
    });
    const ability = useAbility();
    const [addingType, setAddingType] = useState<RefType | null>(null);
    const [inputValue, setInputValue] = useState('');
    // The add menu (link existing / create new) and the type it is open for.
    const [addMenu, setAddMenu] = useState<{
        type: RefType;
        anchor: HTMLElement;
    } | null>(null);
    // A Fund or Probe picked in the add box that already references another
    // Befund — held until the user confirms replacing its link (each may have
    // only one).
    const [replaceSingle, setReplaceSingle] = useState<RefRecord | null>(null);
    // A Gesamtbeschreibung queued for removal — held until the user confirms,
    // because unlinking it here drops the WHOLE Befund from it, not just this
    // one description.
    const [removeOverall, setRemoveOverall] = useState<RefRecord | null>(null);

    // Link a Fund or Probe to THIS description as its single Befund: this
    // description in the matching id array, the other ref arrays cleared.
    async function linkSingle(record: RefRecord) {
        const collection = collectionByType[record.type];
        if (!collection) {
            return;
        }
        const doc = await collection.findOne(record.id).exec();
        if (!doc) {
            return;
        }
        await doc.incrementalModify((data: Record<string, unknown>) => {
            data.planumIds = idField === 'planumIds' ? [descriptionId] : [];
            data.profileIds = idField === 'profileIds' ? [descriptionId] : [];
            // A Probe has no whole-Befund refs to clear.
            if (record.type === 'find') {
                data.findingIds = [];
            }
            data.updatedAt = new Date().toISOString();
            return data;
        });
    }

    // Route a picked reference to the write path: a Fund or Probe replaces its
    // single Befund (asking first when it already has one); other types append
    // as usual.
    function addRef(record: RefRecord) {
        if (SINGLE_REF_TYPES.includes(record.type)) {
            if (record.hasBefund) {
                setReplaceSingle(record);
            } else {
                void linkSingle(record);
            }
            return;
        }
        void toggleRef(record, true);
    }

    // Add/remove this description's id on the target record — persisted straight
    // away (the section reads live and is not part of this editor's form save).
    async function toggleRef(record: RefRecord, shouldLink: boolean) {
        const collection = collectionByType[record.type];
        if (!collection) {
            return;
        }
        const doc = await collection.findOne(record.id).exec();
        if (!doc) {
            return;
        }
        await doc.incrementalModify((data: Record<string, unknown>) => {
            // A Gesamtbeschreibung covers the whole Befund, so the id written
            // here is the BEFUND's — and any per-description refs left over from
            // before that rule are folded into it by the same write.
            if (record.type === 'overallDescription') {
                const next = overallDescriptionRefs(data, shouldLink);
                const before = [
                    ...((data.planumIds as string[] | undefined) ?? []),
                    ...((data.profileIds as string[] | undefined) ?? []),
                    ...((data.findingIds as string[] | undefined) ?? [])
                ];
                const unchanged =
                    before.length === next.findingIds.length &&
                    next.findingIds.every((id) => before.includes(id));
                if (unchanged) {
                    return data;
                }
                data.planumIds = next.planumIds;
                data.profileIds = next.profileIds;
                data.findingIds = next.findingIds;
                data.updatedAt = new Date().toISOString();
                return data;
            }
            const current = (data[idField] as string[] | undefined) ?? [];
            const has = current.includes(descriptionId);
            if (shouldLink && !has) {
                data[idField] = [...current, descriptionId];
                data.updatedAt = new Date().toISOString();
            } else if (!shouldLink && has) {
                data[idField] = current.filter((id) => id !== descriptionId);
                data.updatedAt = new Date().toISOString();
            }
            return data;
        });
    }

    // Where „Neu anlegen“ leads for a type, or null when this user may not
    // create one here or this app has no editor for it.
    const createTo = (type: RefType): string | null =>
        ability.can(
            'create',
            scopedSubject(TYPE_META[type].subject, { projectId })
        )
            ? newRecordTo[type]
            : null;

    // Read-only + nothing referenced: one line, no empty per-type scaffolding.
    if (!canEdit && linkedTotal === 0) {
        return (
            <Box>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                    <FormattedMessage id="descriptionRefs.title" />
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage id="descriptionRefs.empty" />
                </Typography>
            </Box>
        );
    }

    const menuCreateTo = addMenu ? createTo(addMenu.type) : null;

    // While editing every type shows (so an empty one can still be added to);
    // read-only shows only the types that actually have references.
    const shownTypes = TYPE_ORDER.filter(
        (type) => canEdit || (byType.get(type)?.linked.length ?? 0) > 0
    );

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
                <FormattedMessage id="descriptionRefs.title" />
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    columnGap: 3,
                    rowGap: 2,
                    // A vertical rule down the middle of the column gap (sm+ only,
                    // where the two columns sit side by side).
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        display: { xs: 'none', sm: 'block' },
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: '50%',
                        borderLeft: 1,
                        borderColor: 'divider'
                    }
                }}
            >
                {shownTypes.map((type) => {
                    const meta = TYPE_META[type];
                    const bucket = byType.get(type) ?? {
                        linked: [],
                        available: []
                    };
                    const typeLabel = intl.formatMessage({
                        id: meta.typeLabelId
                    });
                    return (
                        <Box key={type}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    minHeight: 32
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 600,
                                        color: 'text.primary'
                                    }}
                                >
                                    {bucket.linked.length > 0
                                        ? `${typeLabel} (${bucket.linked.length})`
                                        : typeLabel}
                                </Typography>
                                {canEdit && addingType !== type && (
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: 'descriptionRefs.add'
                                        })}
                                    >
                                        {/* Wrapped so the tooltip still shows on
                                            the disabled (nothing to add) button. */}
                                        <span>
                                            <IconButton
                                                size="small"
                                                aria-label={intl.formatMessage({
                                                    id: 'descriptionRefs.add'
                                                })}
                                                aria-haspopup="menu"
                                                sx={addOutlineSx}
                                                disabled={
                                                    bucket.available.length ===
                                                        0 && !createTo(type)
                                                }
                                                onClick={(event) =>
                                                    setAddMenu({
                                                        type,
                                                        anchor: event.currentTarget
                                                    })
                                                }
                                            >
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Box>
                            {bucket.linked.length === 0 &&
                                addingType !== type && (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        <FormattedMessage
                                            id="descriptionRefs.emptyType"
                                            values={{ type: typeLabel }}
                                        />
                                    </Typography>
                                )}
                            {bucket.linked.length > 0 && (
                                // Clear of the heading row: a row's hover
                                // tint reaches full width, and flush it
                                // would touch the add button above it.
                                <List dense disablePadding sx={{ mt: 0.5 }}>
                                    {bucket.linked.map((item) => (
                                        <ListItem
                                            key={item.id}
                                            disablePadding
                                            secondaryAction={
                                                canEdit ? (
                                                    <Tooltip
                                                        title={intl.formatMessage(
                                                            {
                                                                id: 'descriptionRefs.remove'
                                                            }
                                                        )}
                                                    >
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            aria-label={intl.formatMessage(
                                                                {
                                                                    id: 'descriptionRefs.remove'
                                                                }
                                                            )}
                                                            onClick={() => {
                                                                /**
                                                                 * Removing a
                                                                 * Gesamtbeschreibung
                                                                 * drops the whole
                                                                 * Befund from it,
                                                                 * so it asks first.
                                                                 */
                                                                if (
                                                                    item.type ===
                                                                    'overallDescription'
                                                                ) {
                                                                    setRemoveOverall(
                                                                        item
                                                                    );
                                                                    return;
                                                                }
                                                                void toggleRef(
                                                                    item,
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            <LinkOffIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : undefined
                                            }
                                        >
                                            {/* Not openable from here (see
                                                RefRecord.to): the row still
                                                documents the link, it just
                                                isn't a button. Same box metrics
                                                either way, so the two kinds of
                                                row line up — but faded, or it
                                                reads as a link that ignores
                                                taps. 0.6 rather than the theme's
                                                0.38 disabled fade: unmistakably
                                                inactive, while the numbers stay
                                                readable in daylight. */}
                                            {item.to ? (
                                                <ListItemButton
                                                    disableGutters
                                                    component={RouterLink}
                                                    to={item.to}
                                                    onClick={linkClick(() =>
                                                        onNavigate(item.to!)
                                                    )}
                                                    sx={{
                                                        borderRadius: 1,
                                                        px: 1,
                                                        pr: canEdit ? 5 : 1
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{ minWidth: 32 }}
                                                    >
                                                        {meta.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item.primary}
                                                        secondary={
                                                            item.secondary
                                                        }
                                                    />
                                                </ListItemButton>
                                            ) : (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        width: '100%',
                                                        px: 1,
                                                        py: 0.5,
                                                        pr: canEdit ? 5 : 1,
                                                        cursor: 'default',
                                                        opacity: 0.6
                                                    }}
                                                >
                                                    <ListItemIcon
                                                        sx={{ minWidth: 32 }}
                                                    >
                                                        {meta.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item.primary}
                                                        secondary={
                                                            item.secondary
                                                        }
                                                    />
                                                </Box>
                                            )}
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                            {canEdit && addingType === type && (
                                <Autocomplete
                                    options={bucket.available}
                                    value={null}
                                    inputValue={inputValue}
                                    onInputChange={(_event, next, reason) =>
                                        setInputValue(
                                            reason === 'reset' ? '' : next
                                        )
                                    }
                                    getOptionLabel={(option) => option.primary}
                                    filterOptions={REF_SEARCH}
                                    isOptionEqualToValue={(option, candidate) =>
                                        option.key === candidate.key
                                    }
                                    onChange={(_event, option) => {
                                        if (option) {
                                            addRef(option);
                                            setInputValue('');
                                        }
                                    }}
                                    onBlur={() => {
                                        setAddingType(null);
                                        setInputValue('');
                                    }}
                                    renderOption={(props, option) => {
                                        const { key, ...rest } = props;
                                        return (
                                            <Box
                                                component="li"
                                                key={key}
                                                {...rest}
                                            >
                                                <ListItemIcon
                                                    sx={{ minWidth: 32 }}
                                                >
                                                    {meta.icon}
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={option.primary}
                                                    secondary={option.secondary}
                                                />
                                            </Box>
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            autoFocus
                                            size="small"
                                            label={intl.formatMessage({
                                                id: 'descriptionRefs.add'
                                            })}
                                        />
                                    )}
                                    sx={{ mt: 0.5, maxWidth: 480 }}
                                />
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Menu
                anchorEl={addMenu?.anchor}
                open={!!addMenu}
                onClose={() => setAddMenu(null)}
            >
                <MenuItem
                    disabled={
                        !addMenu ||
                        (byType.get(addMenu.type)?.available.length ?? 0) === 0
                    }
                    onClick={() => {
                        if (addMenu) {
                            setAddingType(addMenu.type);
                            setInputValue('');
                        }
                        setAddMenu(null);
                    }}
                >
                    <ListItemIcon>
                        <AddLinkIcon fontSize="small" />
                    </ListItemIcon>
                    <FormattedMessage id="descriptionRefs.linkExisting" />
                </MenuItem>
                {/* A real anchor while it leads somewhere — and a guarded
                    navigation, since this editor may hold unsaved edits. */}
                {menuCreateTo ? (
                    <MenuItem
                        component={RouterLink}
                        to={menuCreateTo}
                        onClick={linkClick(() => {
                            setAddMenu(null);
                            onNavigate(menuCreateTo);
                        })}
                    >
                        <ListItemIcon>
                            <NoteAddOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <FormattedMessage id="descriptionRefs.createNew" />
                    </MenuItem>
                ) : (
                    <MenuItem disabled>
                        <ListItemIcon>
                            <NoteAddOutlinedIcon fontSize="small" />
                        </ListItemIcon>
                        <FormattedMessage id="descriptionRefs.createNew" />
                    </MenuItem>
                )}
            </Menu>
            <ConfirmDialog
                open={!!replaceSingle}
                title={
                    <FormattedMessage
                        id={
                            replaceSingle?.type === 'sample'
                                ? 'descriptionRefs.replaceSample.title'
                                : 'descriptionRefs.replaceFind.title'
                        }
                    />
                }
                message={
                    <FormattedMessage
                        id={
                            replaceSingle?.type === 'sample'
                                ? 'descriptionRefs.replaceSample.message'
                                : 'descriptionRefs.replaceFind.message'
                        }
                    />
                }
                confirmLabel={
                    <FormattedMessage id="descriptionRefs.replaceFind.replace" />
                }
                cancelLabel={
                    <FormattedMessage id="descriptionRefs.replaceFind.keep" />
                }
                onConfirm={() => {
                    if (replaceSingle) {
                        void linkSingle(replaceSingle);
                    }
                    setReplaceSingle(null);
                }}
                onCancel={() => setReplaceSingle(null)}
            />
            <ConfirmDialog
                open={!!removeOverall}
                title={
                    <FormattedMessage id="descriptionRefs.removeOverall.title" />
                }
                message={
                    <FormattedMessage
                        id="descriptionRefs.removeOverall.message"
                        values={{
                            number: removeOverall?.primary ?? '',
                            finding: findingNumber || '—'
                        }}
                    />
                }
                confirmLabel={
                    <FormattedMessage id="descriptionRefs.removeOverall.remove" />
                }
                cancelLabel={
                    <FormattedMessage id="descriptionRefs.removeOverall.keep" />
                }
                onConfirm={() => {
                    if (removeOverall) {
                        void toggleRef(removeOverall, false);
                    }
                    setRemoveOverall(null);
                }}
                onCancel={() => setRemoveOverall(null)}
            />
        </Box>
    );
}
